import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Dialog, { type DialogProps } from "@mui/material/Dialog";
import DialogContent, { type DialogContentProps } from "@mui/material/DialogContent";
import DialogTitle, { type DialogTitleProps } from "@mui/material/DialogTitle";
import DialogActions, { type DialogActionsProps } from "@mui/material/DialogActions";
import Button from "ui/base/Button";
import Typography from "@mui/material/Typography";
import type { ButtonProps } from "@mui/material/Button";


type TitleProps = {
	onClose?: () => void;
} & DialogTitleProps;

type ConfirmationAction = {
	title: string;
	text?: string;
	actions: ButtonProps & {
		label: string;
	}[];
	onClose?: () => void;
} & DialogProps;

Modal.Title = ({ children, onClose, ...props }: TitleProps) => {
	const style = {
		root: {
			display: "flex",
			justifyContent: "space-between",
			alignItems: "center",
			p: 2
		}
	};
	return (
		<DialogTitle variant="h2" color="text.primary" {...props} sx={{ ...style.root, ...props.sx }}>
			{children}
			{onClose && <Button.Icon icon="x" color="default" size={28} onClick={onClose} sx={{ p: 0 }} />}
		</DialogTitle>
	);
};

Modal.Content = (props: DialogContentProps) => {
	const style = {
		root: {
			display: "flex",
			flexDirection: "column",
			gap: 2,
			p: 2,
			overflow: "auto",
			...props.sx
		}
	} as SxProps;

	return <DialogContent {...props} sx={style.root} />;
};

Modal.Actions = (props: DialogActionsProps) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(t => t.breakpoints.down("md"));

	const style = {
		root: {
			flexDirection: fullScreen ? "column" : "row",
			gap: 2,
			alignItems: "stretch",
			p: theme.spacing(0, 2, 2),
			...props.sx
		}
	} as SxProps;

	return <DialogActions {...props} sx={style.root} />;
};

Modal.Confirmation = ({ title, text, actions, onClose, ...props }: ConfirmationAction) => (
	<Modal {...props}>
		{title && <Modal.Title onClose={onClose}>{title}</Modal.Title>}
		<Modal.Content>
			<Typography variant="body2">
				{text}
			</Typography>
		</Modal.Content>
		<Modal.Actions>
			{actions.map(({ label, ...action }, i) => <Button variant="contained" {...action} key={i}>{label}</Button>)}
		</Modal.Actions>
	</Modal>
);

export default function Modal({ children, maxWidth = "sm", ...props }: DialogProps) {
	const fullScreen = useMediaQuery(t => t.breakpoints.down("sm"));

	const style = {
		paper: {
			borderRadius: fullScreen ? 0 : "16px"
		}
	};

	return (
		<Dialog fullScreen={fullScreen} slotProps={{ paper: { sx: style.paper } }} fullWidth maxWidth={maxWidth} {...props}>
			{children}
		</Dialog>
	);
}